import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Paper,
  Grid,
  Card,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { apiFetch } from '../../api/client';
import { ESPECIALIDADES_MEDICAS } from '../../constants/especialidadesMedicas';
import {
  TIPOS_CONTRATO,
  CATEGORIAS_SERVICO,
  REGIMES_MEDICO,
  TIPOS_MATERIAL_GRAFICA,
  PERIODICIDADES,
  REGIMES_VALOR,
  emptyDetalhesServico,
} from '../../constants/prestadorClassificacao';
import { parseValorMoedaBr } from '../../utils/parseValorMoeda';
import {
  SETORES_PRESTADOR,
  formatCNPJ,
  cleanCurrency,
  buildEmpresaExibicao,
  prestadorToWizardForm,
} from '../../utils/prestadorFormUtils';

const STEPS_BASE = ['Empresa', 'Classificação', 'Contrato', 'Portal'];
const STEPS_RATEIO = ['Empresa', 'Classificação', 'Contrato', 'Rateio', 'Portal'];

const initialForm = {
  empresa: '',
  cnpj: '',
  email: '',
  telefone: '',
  numeroContrato: '',
  assinatura: '',
  vigenciaInicio: '',
  vigenciaFim: '',
  valorMes: '',
  setor: '',
  objeto: '',
  eventual: false,
  corporativo: false,
  tipoContrato: 'prestacao_servico',
  categoriaServico: 'outro',
  detalhesServico: emptyDetalhesServico(),
  periodicidade: 'mensal',
  regimeValor: 'valor_fixo',
  numeroParcelas: 12,
  valorPorParcela: '',
  parcelas: 12,
  criarLoginPortal: true,
  senhaPortal: '',
};

export default function PrestadorWizardDialog({
  open,
  onClose,
  onSaved,
  unidadeSelecionada,
  prestador = null,
  podeRatear = false,
  unidades = [],
}) {
  const isEdit = Boolean(prestador?.id);
  const isFilhoRateio = Boolean(prestador?.eh_rateio_filho);
  const steps = podeRatear && !isFilhoRateio ? STEPS_RATEIO : STEPS_BASE;
  const rateioStep = podeRatear && !isFilhoRateio ? 3 : -1;
  const portalStep = steps.length - 1;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [portalResult, setPortalResult] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [rateioModo, setRateioModo] = useState('percentual');
  const [rateios, setRateios] = useState([]); // [{ unidade, percentual, valorMesUnidade, selected }]
  const [unidadeMatriz, setUnidadeMatriz] = useState('');
  const [itens, setItens] = useState([]);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError('');
    setPortalResult(null);
    setRateioModo('percentual');
    setItens([]);
    setUnidadeMatriz(unidadeSelecionada?.codigo || '');
    setRateios(
      (unidades || [])
        .filter((u) => u?.codigo && String(u.codigo).toUpperCase() !== 'CORPORATIVO')
        .map((u) => ({
          unidade: u.codigo,
          nome: u.nome || u.codigo,
          selected: false,
          percentual: '',
          valorMesUnidade: '',
        }))
    );

    if (!prestador?.id) {
      setForm(initialForm);
      setItens([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingEdit(true);
      try {
        let portal = {};
        const cnpjNorm = String(prestador.cnpj || '').replace(/\D/g, '');
        if (cnpjNorm.length >= 11) {
          const resPortal = await apiFetch(`/api/admin/prestadores-portal/cnpj/${cnpjNorm}`);
          if (resPortal.ok) {
            const data = await resPortal.json();
            if (data?.encontrado) {
              portal = {
                email: data.email || '',
                telefone: data.telefone || '',
                status: data.status,
                pendenteEmail: data.pendenteEmail
              };
            }
          }
        }
        if (prestador.eh_contrato_matriz || prestador.eh_rateio_filho) {
          const resR = await apiFetch(`/api/prestadores/${prestador.eh_rateio_filho ? prestador.contrato_matriz_id : prestador.id}/rateios`);
          if (resR.ok) {
            const dataR = await resR.json();
            const map = Object.fromEntries((dataR.rateios || []).map((r) => [r.unidade, r]));
            if (!cancelled) {
              setRateioModo((dataR.rateios?.[0]?.modo) || 'percentual');
              setUnidadeMatriz(prestador.unidade || unidadeSelecionada?.codigo || '');
              setRateios((prev) =>
                prev.map((row) => {
                  const r = map[row.unidade];
                  if (!r) return row;
                  return {
                    ...row,
                    selected: true,
                    percentual: r.percentual != null ? String(r.percentual) : '',
                    valorMesUnidade: r.valorMesUnidade || '',
                  };
                })
              );
            }
          }
        }
        try {
          const resItens = await apiFetch(`/api/prestadores/${prestador.id}/itens`);
          if (resItens.ok) {
            const dataItens = await resItens.json();
            if (!cancelled) setItens(Array.isArray(dataItens) ? dataItens : []);
          }
        } catch {
          if (!cancelled) setItens([]);
        }
        if (!cancelled) {
          setForm(prestadorToWizardForm(prestador, portal));
        }
      } catch {
        if (!cancelled) setForm(prestadorToWizardForm(prestador, {}));
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, prestador, unidades]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'cnpj') v = formatCNPJ(v);
    setForm((p) => ({ ...p, [name]: v }));
  };

  const handleDetalhe = (name, value) => {
    setForm((p) => ({
      ...p,
      detalhesServico: { ...p.detalhesServico, [name]: value },
    }));
  };

  const empresaExibicao = () => buildEmpresaExibicao(form);

  const validarPasso = () => {
    if (step === 0) {
      if (!form.empresa || !form.cnpj) {
        setError('Informe razão social/empresa e CNPJ.');
        return false;
      }
      if (form.cnpj.replace(/\D/g, '').length < 14) {
        setError('CNPJ inválido.');
        return false;
      }
      const emailNorm = form.email?.trim();
      if (emailNorm && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailNorm)) {
        setError('E-mail inválido.');
        return false;
      }
    }
    if (step === 1) {
      if (!form.tipoContrato || !form.categoriaServico) {
        setError('Selecione o tipo de contrato e a categoria de serviço.');
        return false;
      }
      const d = form.detalhesServico || {};
      if (form.categoriaServico === 'servico_medico') {
        if (!d.especialidades?.length) {
          setError('Selecione ao menos uma especialidade médica.');
          return false;
        }
        if (!d.horasMensais || !d.quantidadeMedicos) {
          setError('Preencha quantidade de médicos e horas mensais.');
          return false;
        }
      }
      if (form.categoriaServico === 'servico_odontologico' && !d.quantidadeDentistas) {
        setError('Informe a quantidade de dentistas.');
        return false;
      }
      if (form.tipoContrato === 'terceirizado' && !d.quantidadeFuncionarios) {
        setError('Informe a quantidade de funcionários alocados.');
        return false;
      }
      if (form.tipoContrato === 'locacao' && !d.enderecoImovel) {
        setError('Informe o endereço/identificação do imóvel.');
        return false;
      }
      if (form.tipoContrato === 'comodato' && !d.descricaoBem) {
        setError('Descreva o bem cedido em comodato.');
        return false;
      }
    }
    if (step === 2) {
      if (
        !form.numeroContrato ||
        !form.assinatura ||
        !form.vigenciaInicio ||
        !form.vigenciaFim ||
        !form.valorMes ||
        !form.setor ||
        !form.objeto
      ) {
        setError('Preencha todos os campos do contrato.');
        return false;
      }
    }
    if (step === rateioStep && rateioStep >= 0) {
      const selected = rateios.filter((r) => r.selected);
      if (selected.length) {
        if (!unidadeMatriz) {
          setError('Selecione a unidade matriz do contrato.');
          return false;
        }
        if (rateioModo === 'sem_rateio') {
          setError('');
          return true;
        }
        const total = Number(cleanCurrency(form.valorMes)) || 0;
        if (rateioModo === 'percentual') {
          // Aceita vírgula BR ("2,73") — Number("2,73") vira NaN e só conta inteiros como "3"
          const sum = selected.reduce((a, r) => a + (parseValorMoedaBr(r.percentual) || 0), 0);
          if (Math.abs(sum - 100) > 0.05) {
            setError(`A soma dos percentuais deve ser 100% (atual: ${sum.toFixed(2)}%).`);
            return false;
          }
        } else {
          const sum = selected.reduce((a, r) => a + (parseValorMoedaBr(r.valorMesUnidade) || 0), 0);
          if (Math.abs(sum - total) > 0.05) {
            setError('A soma dos valores deve igualar o valor do contrato.');
            return false;
          }
        }
      }
    }
    if (step === portalStep && form.criarLoginPortal && form.senhaPortal && form.senhaPortal.length < 4) {
      setError('Senha do portal deve ter ao menos 4 caracteres.');
      return false;
    }
    setError('');
    return true;
  };

  const avancar = () => {
    if (!validarPasso()) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const voltar = () => setStep((s) => Math.max(s - 1, 0));

  const handleClose = () => {
    setStep(0);
    setForm(initialForm);
    setItens([]);
    setError('');
    setPortalResult(null);
    onClose();
  };

  const handleAddItem = () => {
    setItens((prev) => [
      ...prev,
      { descricao: '', unidade: 'UND', quantidade: '', valor_unitario: '', total: 0 },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItens((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setItens((prev) => {
      const novos = [...prev];
      novos[index] = { ...novos[index], [field]: value };
      if (field === 'quantidade' || field === 'valor_unitario') {
        const q = parseFloat(novos[index].quantidade) || 0;
        const v = parseFloat(novos[index].valor_unitario) || 0;
        novos[index].total = q * v;
      }
      return novos;
    });
  };

  const buildPayload = () => {
    const d = form.detalhesServico || emptyDetalhesServico();
    return {
      numeroContrato: form.numeroContrato,
      empresa: empresaExibicao(),
      cnpj: form.cnpj.replace(/\D/g, ''),
      assinatura: form.assinatura,
      vigenciaInicio: form.vigenciaInicio,
      vigenciaFim: form.vigenciaFim,
      valorMes: cleanCurrency(form.valorMes),
      setor: form.setor,
      objeto: form.objeto,
      eventual: form.eventual,
      corporativo: form.corporativo,
      tipoContrato: form.tipoContrato,
      categoriaServico: form.categoriaServico,
      detalhesServico: d,
      periodicidade: form.periodicidade || 'mensal',
      regimeValor: form.regimeValor || 'valor_fixo',
      numeroParcelas:
        form.periodicidade === 'parcelado'
          ? Number(form.numeroParcelas) || Number(form.parcelas) || 12
          : form.numeroParcelas || null,
      valorPorParcela: form.periodicidade === 'parcelado' ? form.valorPorParcela : null,
      especialidade: d.especialidades?.[0] || '',
      horasMensais: d.horasMensais,
      horasSemanais: d.horasSemanais,
      quantidadeMedicos: d.quantidadeMedicos,
      quantidadeFuncionarios: d.quantidadeFuncionarios,
      parcelas: form.parcelas || form.numeroParcelas || 12,
      itens: (itens || [])
        .filter((it) => String(it.descricao || '').trim())
        .map((it) => ({
          descricao: String(it.descricao).trim(),
          unidade: it.unidade || 'UND',
          quantidade: Number(it.quantidade) || 0,
          valor_unitario: Number(it.valor_unitario) || 0,
          total:
            it.total != null
              ? Number(it.total)
              : (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0),
        })),
      unidade: unidadeSelecionada?.codigo || null,
      unidadeMatriz: rateios.some((r) => r.selected) ? unidadeMatriz || null : null,
      rateios: rateios
        .filter((r) => r.selected)
        .map((r) => ({
          unidade: r.unidade,
          modo: rateioModo,
          percentual:
            rateioModo === 'percentual' ? parseValorMoedaBr(r.percentual) ?? 0 : undefined,
          valorMesUnidade:
            rateioModo === 'valor' ? parseValorMoedaBr(r.valorMesUnidade) ?? 0 : undefined,
        })),
    };
  };

  const finalizar = async () => {
    if (!validarPasso()) return;
    setSaving(true);
    setError('');
    try {
      const dataToSend = buildPayload();
      const url = isEdit ? `/api/prestadores/${prestador.id}` : '/api/prestadores';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao salvar contrato');
      }

      let portalMsg = null;
      const emailNorm = form.email?.trim();
      const resPortal = await apiFetch('/api/admin/prestadores-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          razaoSocial: form.empresa,
          cnpj: form.cnpj.replace(/\D/g, ''),
          email: emailNorm || undefined,
          telefone: form.telefone,
          senhaInicial: form.senhaPortal || undefined,
          unidade: unidadeSelecionada?.codigo || null
        })
      });
      const portalData = await resPortal.json().catch(() => ({}));
      if (!resPortal.ok) {
        throw new Error(portalData.error || 'Contrato salvo, mas falhou ao sincronizar o portal do prestador.');
      }
      portalMsg = portalData;
      setPortalResult(portalData);

      onSaved?.({ portal: portalMsg, edit: isEdit });
    } catch (e) {
      setError(e.message || 'Erro ao concluir cadastro.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar prestador — assistente' : 'Novo prestador — assistente de cadastro'}
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {isFilhoRateio && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Contrato rateado pela Matriz — a estrutura não é editável nesta unidade. Use Parcelas para pagamentos da fatia.
          </Alert>
        )}

        {loadingEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Carregando dados do contrato...
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {portalResult && step === portalStep && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Login do portal {isEdit ? 'atualizado' : 'criado'}.
            {portalResult.senhaInicial && (
              <>
                {' '}
                Senha: <strong>{portalResult.senhaInicial}</strong>
              </>
            )}
          </Alert>
        )}

        {!loadingEdit && step === 0 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Razão social / Empresa"
              name="empresa"
              value={form.empresa}
              onChange={handleChange}
              fullWidth
              required
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField label="CNPJ" name="cnpj" value={form.cnpj} onChange={handleChange} fullWidth required />
            <TextField
              label="E-mail (portal e contato)"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              helperText="Opcional. Sem e-mail, o CNPJ fica pendente no portal até você informá-lo. O e-mail de login/senha não é enviado agora — será encaminhado posteriormente."
            />
            <TextField label="Telefone" name="telefone" value={form.telefone} onChange={handleChange} fullWidth />
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
              Um CNPJ = um prestador no portal. Contratos com o mesmo CNPJ aparecem automaticamente para esse login.
            </Typography>
          </Box>
        )}

        {!loadingEdit && step === 1 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              label="Tipo de contrato"
              name="tipoContrato"
              value={form.tipoContrato}
              onChange={handleChange}
              fullWidth
              required
            >
              {TIPOS_CONTRATO.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Categoria de serviço"
              name="categoriaServico"
              value={form.categoriaServico}
              onChange={handleChange}
              fullWidth
              required
            >
              {CATEGORIAS_SERVICO.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Checkbox name="corporativo" checked={form.corporativo} onChange={handleChange} />}
              label="Contrato corporativo (visão multiunidade)"
            />

            {form.categoriaServico === 'servico_medico' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalhes — Serviço médico
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    select
                    label="Especialidade principal"
                    value={form.detalhesServico.especialidades?.[0] || ''}
                    onChange={(e) => handleDetalhe('especialidades', e.target.value ? [e.target.value] : [])}
                    fullWidth
                    required
                  >
                    {ESPECIALIDADES_MEDICAS.map((e) => (
                      <MenuItem key={e} value={e}>
                        {e}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Qtd. médicos"
                    type="number"
                    value={form.detalhesServico.quantidadeMedicos}
                    onChange={(e) => handleDetalhe('quantidadeMedicos', e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Horas médicas / mês"
                    type="number"
                    value={form.detalhesServico.horasMensais}
                    onChange={(e) => handleDetalhe('horasMensais', e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Carga horária semanal"
                    type="number"
                    value={form.detalhesServico.horasSemanais}
                    onChange={(e) => handleDetalhe('horasSemanais', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    select
                    label="Regime"
                    value={form.detalhesServico.regimeMedico}
                    onChange={(e) => handleDetalhe('regimeMedico', e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">—</MenuItem>
                    {REGIMES_MEDICO.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="CRM do responsável técnico"
                    value={form.detalhesServico.crmResponsavel}
                    onChange={(e) => handleDetalhe('crmResponsavel', e.target.value)}
                    fullWidth
                  />
                </Box>
              </Paper>
            )}

            {form.categoriaServico === 'servico_odontologico' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalhes — Serviço odontológico
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    label="Qtd. dentistas"
                    type="number"
                    value={form.detalhesServico.quantidadeDentistas}
                    onChange={(e) => handleDetalhe('quantidadeDentistas', e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="CRO do responsável técnico"
                    value={form.detalhesServico.croResponsavel}
                    onChange={(e) => handleDetalhe('croResponsavel', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Procedimentos cobertos"
                    value={form.detalhesServico.procedimentosCobertos}
                    onChange={(e) => handleDetalhe('procedimentosCobertos', e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ gridColumn: '1 / -1' }}
                  />
                </Box>
              </Paper>
            )}

            {form.categoriaServico === 'grafica' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalhes — Gráfica
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    select
                    label="Tipo de material"
                    value={form.detalhesServico.tipoMaterial}
                    onChange={(e) => handleDetalhe('tipoMaterial', e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">—</MenuItem>
                    {TIPOS_MATERIAL_GRAFICA.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Volume médio mensal estimado"
                    value={form.detalhesServico.volumeMedioMensal}
                    onChange={(e) => handleDetalhe('volumeMedioMensal', e.target.value)}
                    fullWidth
                  />
                </Box>
              </Paper>
            )}

            {!['servico_medico', 'servico_odontologico', 'grafica'].includes(form.categoriaServico) && (
              <TextField
                label="Observações da categoria"
                value={form.detalhesServico.observacoes}
                onChange={(e) => handleDetalhe('observacoes', e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            )}

            {form.tipoContrato === 'locacao' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalhes — Locação
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    label="Endereço / identificação do imóvel"
                    value={form.detalhesServico.enderecoImovel}
                    onChange={(e) => handleDetalhe('enderecoImovel', e.target.value)}
                    fullWidth
                    required
                    sx={{ gridColumn: '1 / -1' }}
                  />
                  <TextField
                    label="Metragem"
                    value={form.detalhesServico.metragem}
                    onChange={(e) => handleDetalhe('metragem', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Finalidade do espaço"
                    value={form.detalhesServico.finalidadeEspaco}
                    onChange={(e) => handleDetalhe('finalidadeEspaco', e.target.value)}
                    fullWidth
                  />
                </Box>
              </Paper>
            )}

            {form.tipoContrato === 'comodato' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalhes — Comodato
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    label="Descrição do bem cedido"
                    value={form.detalhesServico.descricaoBem}
                    onChange={(e) => handleDetalhe('descricaoBem', e.target.value)}
                    fullWidth
                    required
                    sx={{ gridColumn: '1 / -1' }}
                  />
                  <TextField
                    label="Valor de referência do bem"
                    value={form.detalhesServico.valorReferenciaBem}
                    onChange={(e) => handleDetalhe('valorReferenciaBem', e.target.value)}
                    fullWidth
                  />
                </Box>
              </Paper>
            )}

            {form.tipoContrato === 'terceirizado' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalhes — Terceirizado
                </Typography>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    label="Posto de trabalho"
                    value={form.detalhesServico.postoTrabalho}
                    onChange={(e) => handleDetalhe('postoTrabalho', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Qtd. funcionários alocados"
                    type="number"
                    value={form.detalhesServico.quantidadeFuncionarios}
                    onChange={(e) => handleDetalhe('quantidadeFuncionarios', e.target.value)}
                    fullWidth
                    required
                  />
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {!loadingEdit && step === 2 && (
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <TextField
              label="Nº contrato"
              name="numeroContrato"
              value={form.numeroContrato}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Empresa no contrato"
              value={empresaExibicao()}
              fullWidth
              disabled
              helperText="Nome da empresa (especialidade fica na coluna própria)"
            />
            <TextField
              label="Assinatura"
              name="assinatura"
              type="date"
              value={form.assinatura}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Início vigência"
              name="vigenciaInicio"
              type="date"
              value={form.vigenciaInicio}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Fim vigência"
              name="vigenciaFim"
              type="date"
              value={form.vigenciaFim}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Valor/mês"
              name="valorMes"
              value={form.valorMes}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              select
              label="Setor"
              name="setor"
              value={form.setor}
              onChange={handleChange}
              fullWidth
              required
            >
              {SETORES_PRESTADOR.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Periodicidade de cobrança"
              name="periodicidade"
              value={form.periodicidade}
              onChange={handleChange}
              fullWidth
              required
            >
              {PERIODICIDADES.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Regime de valor"
              name="regimeValor"
              value={form.regimeValor}
              onChange={handleChange}
              fullWidth
            >
              {REGIMES_VALOR.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            {form.periodicidade === 'parcelado' && (
              <>
                <TextField
                  label="Número de parcelas"
                  name="numeroParcelas"
                  type="number"
                  value={form.numeroParcelas}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 120 }}
                  fullWidth
                  required
                />
                <TextField
                  label="Valor por parcela (opcional)"
                  name="valorPorParcela"
                  value={form.valorPorParcela}
                  onChange={handleChange}
                  fullWidth
                  helperText="Se vazio, divide o valor/mês pelo nº de parcelas"
                />
              </>
            )}
            <TextField
              label="Parcelas (legado)"
              name="parcelas"
              type="number"
              value={form.parcelas}
              onChange={handleChange}
              inputProps={{ min: 1, max: 12 }}
              fullWidth
              helperText="Campo legado — use Periodicidade acima"
            />
            <TextField
              label="Objeto"
              name="objeto"
              value={form.objeto}
              onChange={handleChange}
              multiline
              minRows={2}
              fullWidth
              required
              sx={{ gridColumn: '1 / -1' }}
            />
            <Box
              sx={{
                gridColumn: '1 / -1',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">Itens do contrato</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem}>
                  Adicionar item
                </Button>
              </Box>
              {itens.map((item, index) => (
                <Card key={index} sx={{ mb: 2, p: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">Item {index + 1}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Descrição"
                        value={item.descricao}
                        onChange={(e) => handleItemChange(index, 'descricao', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        select
                        label="Unidade"
                        value={item.unidade}
                        onChange={(e) => handleItemChange(index, 'unidade', e.target.value)}
                        fullWidth
                        size="small"
                      >
                        <MenuItem value="KG">KG</MenuItem>
                        <MenuItem value="UND">UND</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Quantidade"
                        type="number"
                        value={item.quantidade}
                        onChange={(e) => handleItemChange(index, 'quantidade', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Valor unitário"
                        type="number"
                        value={item.valor_unitario}
                        onChange={(e) => handleItemChange(index, 'valor_unitario', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
              {itens.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nenhum item adicionado (recomendado para contratos de valor variável / por item).
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {!loadingEdit && step === rateioStep && rateioStep >= 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info">
              {rateioModo === 'sem_rateio'
                ? 'Contrato corporativo: marque só as unidades que também terão o contrato. Sem percentual e sem fatia. O valor conta quando cada unidade lançar a nota fiscal. Na matriz, a nota aparece como Nota nº XXX - UNIDADE.'
                : 'Rateie o valor do contrato entre unidades. A soma deve fechar 100% (ou o valor total). Escolha a unidade matriz — ela guarda o valor integral; as demais ficam com a fatia.'}
            </Alert>
            <TextField
              select
              label="Unidade matriz do contrato"
              value={unidadeMatriz}
              onChange={(e) => setUnidadeMatriz(e.target.value)}
              fullWidth
              required
              helperText={
                rateioModo === 'sem_rateio'
                  ? 'Unidade que vê as notas das demais, no formato Nota nº XXX - UNIDADE.'
                  : 'Dona do valor integral. Não precisa ter fatia no rateio.'
              }
            >
              {rateios.map((r) => (
                <MenuItem key={r.unidade} value={r.unidade}>
                  {r.nome} ({r.unidade})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Modo do rateio"
              value={rateioModo}
              onChange={(e) => {
                setRateioModo(e.target.value);
                setError('');
              }}
              fullWidth
            >
              <MenuItem value="percentual">Percentual (%)</MenuItem>
              <MenuItem value="valor">Valor fixo (R$)</MenuItem>
              <MenuItem value="sem_rateio">Sem rateio</MenuItem>
            </TextField>
            {rateios.map((row) => (
              <Paper key={row.unidade} variant="outlined" sx={{ p: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={row.selected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRateios((prev) =>
                          prev.map((r) =>
                            r.unidade === row.unidade ? { ...r, selected: checked } : r
                          )
                        );
                      }}
                    />
                  }
                  label={`${row.nome} (${row.unidade})${
                    unidadeMatriz === row.unidade ? ' — Matriz' : ''
                  }`}
                />
                {row.selected && rateioModo !== 'sem_rateio' && (
                  <TextField
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    label={rateioModo === 'percentual' ? 'Percentual %' : 'Valor da fatia'}
                    value={rateioModo === 'percentual' ? row.percentual : row.valorMesUnidade}
                    onChange={(e) =>
                      setRateios((prev) =>
                        prev.map((r) =>
                          r.unidade === row.unidade
                            ? {
                                ...r,
                                [rateioModo === 'percentual' ? 'percentual' : 'valorMesUnidade']:
                                  e.target.value,
                              }
                            : r
                        )
                      )
                    }
                  />
                )}
              </Paper>
            ))}
            {!rateios.length && (
              <Typography color="text.secondary">Nenhuma unidade disponível para rateio.</Typography>
            )}
          </Box>
        )}

        {!loadingEdit && step === portalStep && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              O portal é sincronizado ao salvar (login criado/atualizado). O e-mail de login e senha
              <strong> não é enviado automaticamente</strong> — o envio será feito posteriormente
              (em massa). Sem e-mail, o CNPJ permanece pendente no portal até você informá-lo.
            </Alert>
            <TextField
              label="Senha inicial do portal (opcional)"
              name="senhaPortal"
              type="password"
              value={form.senhaPortal}
              onChange={handleChange}
              fullWidth
              helperText="Se vazio, será gerada automaticamente (ex.: SgC0106)"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              O prestador acessa na aba <strong>Prestador</strong> do login com e-mail{' '}
              <strong>{form.email || '(pendente)'}</strong> e CNPJ cadastrado.
            </Typography>
            {portalResult && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'rgba(46,125,50,0.06)' }}>
                <Typography variant="subtitle2" fontWeight={700} color="success.main">
                  Portal sincronizado
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {portalResult.message}
                </Typography>
                {portalResult.senhaInicial && portalResult.status === 'ATIVO' && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Senha: <strong>{portalResult.senhaInicial}</strong>
                  </Typography>
                )}
              </Paper>
            )}
            <PaperResumo form={form} empresa={empresaExibicao()} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {portalResult ? 'Fechar' : 'Cancelar'}
        </Button>
        {step > 0 && !portalResult && (
          <Button onClick={voltar} disabled={saving || loadingEdit}>
            Voltar
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button variant="contained" onClick={avancar} disabled={loadingEdit}>
            Próximo
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={finalizar}
            disabled={saving || loadingEdit || Boolean(portalResult) || isFilhoRateio}
          >
            {saving ? 'Salvando...' : portalResult ? 'Concluído' : isEdit ? 'Salvar alterações' : 'Finalizar cadastro'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function PaperResumo({ form, empresa }) {
  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Resumo
      </Typography>
      <Typography variant="body2">Empresa: {empresa}</Typography>
      <Typography variant="body2">CNPJ: {form.cnpj}</Typography>
      <Typography variant="body2">Contrato: {form.numeroContrato}</Typography>
      <Typography variant="body2">E-mail portal: {form.email || '(pendente)'}</Typography>
    </Box>
  );
}
